import React, { useState, useMemo } from 'react';
import { ALL_YEARS, getDefaultYears, getMonthsForYears } from './CoverageCalendar';

const MONTH_LABELS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

/**
 * AggregatedCoverageCalendar - Shows combined coverage data across ALL departments
 *
 * Displays a single calendar matrix with:
 * - Total POs per month (IT Goods + IT Services combined from all depts)
 * - Color intensity based on total coverage
 * - Tooltips showing breakdown
 */
export default function AggregatedCoverageCalendar({ deptRows }) {
  const [activeYears, setActiveYears] = useState(getDefaultYears);
  const now = new Date();

  const toggleYear = (year) => {
    setActiveYears(prev => {
      const next = prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year].sort();
      return next.length === 0 ? prev : next;
    });
  };

  // Aggregate all departments' data by month
  const aggregatedData = useMemo(() => {
    const monthTotals = {};
    const months = getMonthsForYears(activeYears);

    // Initialize all months
    for (const mk of months) {
      monthTotals[mk] = {
        goods: 0,
        services: 0,
        total: 0,
        deptsWithGoods: 0,
        deptsWithServices: 0,
        deptsWithData: new Set(),
      };
    }

    // Aggregate across all departments
    for (const dept of deptRows) {
      for (const [mk, monthData] of Object.entries(dept.monthMap)) {
        if (!monthTotals[mk]) continue;

        const goodsPos = monthData.acq_types?.['IT Goods']?.pos || 0;
        const servicesPos = monthData.acq_types?.['IT Services']?.pos || 0;

        monthTotals[mk].goods += goodsPos;
        monthTotals[mk].services += servicesPos;
        monthTotals[mk].total += goodsPos + servicesPos;

        if (goodsPos > 0) monthTotals[mk].deptsWithGoods++;
        if (servicesPos > 0) monthTotals[mk].deptsWithServices++;
        if (goodsPos > 0 || servicesPos > 0) {
          monthTotals[mk].deptsWithData.add(dept.department_id);
        }
      }
    }

    // Convert Set to count
    for (const mk of Object.keys(monthTotals)) {
      monthTotals[mk].deptsWithData = monthTotals[mk].deptsWithData.size;
    }

    return monthTotals;
  }, [deptRows, activeYears]);

  // Calculate summary stats
  const summary = useMemo(() => {
    const months = Object.values(aggregatedData);
    const totalPOs = months.reduce((sum, m) => sum + m.total, 0);
    const totalGoods = months.reduce((sum, m) => sum + m.goods, 0);
    const totalServices = months.reduce((sum, m) => sum + m.services, 0);
    const monthsWithData = months.filter(m => m.total > 0).length;
    const avgPOsPerMonth = monthsWithData > 0 ? Math.round(totalPOs / monthsWithData) : 0;
    const maxPOs = Math.max(...months.map(m => m.total), 1);

    return { totalPOs, totalGoods, totalServices, monthsWithData, avgPOsPerMonth, maxPOs, totalMonths: months.length };
  }, [aggregatedData]);

  // Get color intensity based on PO count
  const getIntensity = (total) => {
    if (total === 0) return 'empty';
    const ratio = total / summary.maxPOs;
    if (ratio >= 0.75) return 'high';
    if (ratio >= 0.5) return 'medium';
    if (ratio >= 0.25) return 'low';
    return 'minimal';
  };

  const sortedYears = [...activeYears].sort((a, b) => a - b);

  return (
    <div className="sd-agg-cal">
      {/* Summary stats */}
      <div className="sd-agg-cal-summary">
        <div className="sd-agg-cal-stat">
          <span className="sd-agg-cal-stat-value">{summary.totalPOs.toLocaleString()}</span>
          <span className="sd-agg-cal-stat-label">Total POs</span>
        </div>
        <div className="sd-agg-cal-stat">
          <span className="sd-agg-cal-stat-value sd-agg-cal-goods">{summary.totalGoods.toLocaleString()}</span>
          <span className="sd-agg-cal-stat-label">IT Goods</span>
        </div>
        <div className="sd-agg-cal-stat">
          <span className="sd-agg-cal-stat-value sd-agg-cal-services">{summary.totalServices.toLocaleString()}</span>
          <span className="sd-agg-cal-stat-label">IT Services</span>
        </div>
        <div className="sd-agg-cal-stat">
          <span className="sd-agg-cal-stat-value">{summary.monthsWithData}</span>
          <span className="sd-agg-cal-stat-label">of {summary.totalMonths} months with data</span>
        </div>
        <div className="sd-agg-cal-stat">
          <span className="sd-agg-cal-stat-value">~{summary.avgPOsPerMonth}</span>
          <span className="sd-agg-cal-stat-label">avg POs/month</span>
        </div>
      </div>

      {/* Year selector */}
      <div className="sd-agg-cal-toolbar">
        <div className="sd-agg-cal-years">
          {ALL_YEARS.map(y => (
            <button
              key={y}
              className={`sd-agg-cal-year-pill ${activeYears.includes(y) ? 'sd-agg-cal-year-active' : ''}`}
              onClick={() => toggleYear(y)}
            >
              {y}
            </button>
          ))}
        </div>
      </div>

      {/* Calendar grid */}
      <div className="sd-agg-cal-grid">
        {/* Header row with month labels */}
        <div className="sd-agg-cal-header">
          <div className="sd-agg-cal-year-label"></div>
          {MONTH_LABELS.map(m => (
            <div key={m} className="sd-agg-cal-month-label">{m}</div>
          ))}
        </div>

        {/* Year rows */}
        {sortedYears.map(year => (
          <div key={year} className="sd-agg-cal-row">
            <div className="sd-agg-cal-year-label">{year}</div>
            {MONTH_LABELS.map((_, monthIdx) => {
              const mk = `${year}-${String(monthIdx + 1).padStart(2, '0')}`;
              const data = aggregatedData[mk];
              const isFuture = year > now.getFullYear() || (year === now.getFullYear() && monthIdx > now.getMonth());

              if (isFuture || !data) {
                return <div key={mk} className="sd-agg-cal-cell sd-agg-cal-cell-future" />;
              }

              const intensity = getIntensity(data.total);

              return (
                <div
                  key={mk}
                  className={`sd-agg-cal-cell sd-agg-cal-cell-${intensity}`}
                  title={`${MONTH_LABELS[monthIdx]} ${year}: ${data.total.toLocaleString()} POs total\n• IT Goods: ${data.goods.toLocaleString()} (${data.deptsWithGoods} depts)\n• IT Services: ${data.services.toLocaleString()} (${data.deptsWithServices} depts)\n• ${data.deptsWithData} departments with data`}
                >
                  <div className="sd-agg-cal-cell-content">
                    <span className="sd-agg-cal-cell-total">{data.total > 0 ? data.total : ''}</span>
                    {data.total > 0 && (
                      <div className="sd-agg-cal-cell-split">
                        <span className="sd-agg-cal-cell-goods">{data.goods}</span>
                        <span className="sd-agg-cal-cell-services">{data.services}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="sd-agg-cal-legend">
        <span className="sd-agg-cal-legend-label">Coverage:</span>
        <div className="sd-agg-cal-legend-item">
          <div className="sd-agg-cal-legend-box sd-agg-cal-cell-empty"></div>
          <span>None</span>
        </div>
        <div className="sd-agg-cal-legend-item">
          <div className="sd-agg-cal-legend-box sd-agg-cal-cell-minimal"></div>
          <span>Low</span>
        </div>
        <div className="sd-agg-cal-legend-item">
          <div className="sd-agg-cal-legend-box sd-agg-cal-cell-low"></div>
          <span>25%+</span>
        </div>
        <div className="sd-agg-cal-legend-item">
          <div className="sd-agg-cal-legend-box sd-agg-cal-cell-medium"></div>
          <span>50%+</span>
        </div>
        <div className="sd-agg-cal-legend-item">
          <div className="sd-agg-cal-legend-box sd-agg-cal-cell-high"></div>
          <span>75%+</span>
        </div>
        <div className="sd-agg-cal-legend-sep">|</div>
        <div className="sd-agg-cal-legend-item">
          <span className="sd-agg-cal-goods-dot"></span>
          <span>Goods</span>
        </div>
        <div className="sd-agg-cal-legend-item">
          <span className="sd-agg-cal-services-dot"></span>
          <span>Services</span>
        </div>
      </div>
    </div>
  );
}
